import { useEffect, useState } from 'react'
import { Heart, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { FadeModal } from '@/components/ui/fade-modal'
import {
  addPropertyToCollection,
  createCollectionAndAddProperty,
  listCollections,
  toggleFavourite,
} from '@/lib/collections'
import { callableErrorMessage } from '@/lib/callable-errors'
import type { PropertyCollection } from '@/types/models'

type Mode = 'menu' | 'pick' | 'create'

export function SaveToCollectionSheet({
  open,
  onClose,
  uid,
  propertyId,
  fullAddress,
  favourited,
  onFavouritedChange,
}: {
  open: boolean
  onClose: () => void
  uid: string
  propertyId: string
  fullAddress: string
  favourited: boolean
  onFavouritedChange: (next: boolean) => void
}) {
  const [mode, setMode] = useState<Mode>('menu')
  const [collections, setCollections] = useState<PropertyCollection[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setMode('menu')
      setError(null)
      setNotice(null)
      setName('')
      setDescription('')
      return
    }
  }, [open])

  async function loadCollections() {
    setLoading(true)
    setError(null)
    try {
      const list = await listCollections(uid)
      setCollections(list.filter((c) => !c.isFavourites))
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  async function onToggleFavourite() {
    setBusy(true)
    setError(null)
    try {
      const { favourited: next } = await toggleFavourite(uid, propertyId, fullAddress)
      onFavouritedChange(next)
      setNotice(next ? 'Added to Favourites' : 'Removed from Favourites')
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onPickCollection(collectionId: string) {
    setBusy(true)
    setError(null)
    try {
      await addPropertyToCollection(uid, collectionId, propertyId, fullAddress)
      setNotice('Added to collection')
      window.setTimeout(onClose, 600)
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onCreate() {
    setBusy(true)
    setError(null)
    try {
      await createCollectionAndAddProperty(
        uid,
        { name, description },
        propertyId,
        fullAddress,
      )
      setNotice('Collection created')
      window.setTimeout(onClose, 600)
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <FadeModal
      open={open}
      onClose={onClose}
      labelledBy="save-collection-title"
      className="z-[80]"
    >
      <Card className="relative border-0 shadow-2xl">
        <CardHeader>
          <CardTitle id="save-collection-title">
            {mode === 'menu'
              ? 'Save property'
              : mode === 'pick'
                ? 'Add to a collection'
                : 'New collection'}
          </CardTitle>
          <CardDescription>
            {mode === 'menu'
              ? 'Favourite this property or add it to a collection.'
              : mode === 'pick'
                ? 'Choose a collection for this property.'
                : 'Give your collection a unique name.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {mode === 'menu' ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
                onClick={() => void onToggleFavourite()}
              >
                <Heart
                  className={favourited ? 'fill-biddle-blue text-biddle-blue' : ''}
                />
                {favourited ? 'Remove from Favourites' : 'Favourite'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start"
                disabled={busy}
                onClick={() => {
                  setMode('pick')
                  void loadCollections()
                }}
              >
                <Plus />
                Add to a collection…
              </Button>
            </>
          ) : null}

          {mode === 'pick' ? (
            loading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <>
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  disabled={busy}
                  onClick={() => setMode('create')}
                >
                  New collection
                </Button>
                {collections.length === 0 ? (
                  <p className="text-sm text-muted">No other collections yet.</p>
                ) : (
                  <ul className="max-h-56 space-y-1 overflow-y-auto">
                    {collections.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void onPickCollection(c.id)}
                          className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition hover:bg-surface"
                        >
                          <span className="font-medium text-navy">{c.name}</span>
                          <span className="text-muted">
                            {c.itemCount} {c.itemCount === 1 ? 'property' : 'properties'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Button type="button" variant="ghost" onClick={() => setMode('menu')}>
                  Back
                </Button>
              </>
            )
          ) : null}

          {mode === 'create' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="collection-name">Name</Label>
                <Input
                  id="collection-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Weekend inspections"
                  maxLength={60}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="collection-desc">Description (optional)</Label>
                <Textarea
                  id="collection-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this collection for?"
                  maxLength={280}
                  className="min-h-[80px]"
                />
              </div>
              <div className="flex justify-between gap-2">
                <Button type="button" variant="ghost" onClick={() => setMode('pick')}>
                  Back
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={busy || !name.trim()}
                  onClick={() => void onCreate()}
                >
                  {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                  Create & add
                </Button>
              </div>
            </>
          ) : null}

          {error ? <p className="text-sm text-danger">{error}</p> : null}
          {notice ? <p className="text-sm text-success">{notice}</p> : null}

          {mode === 'menu' ? (
            <Button type="button" variant="ghost" className="w-full" onClick={onClose}>
              Cancel
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </FadeModal>
  )
}
